import { makeAutoObservable } from 'mobx';
import { IOrderRow } from '../components/tables/OrderRow';
import { IRetrieveOrdersRequest, IUserOrdersServiceProvider } from '../providers';
import { IOrdersListItem } from '../providers/interface';
import { IHandlerList } from '../../../../../interfaces';
import { logger } from '@qlean/front-logger';
import { plt } from '../../../../../services/GrpcService';
import { OrderStatusToTextMapping } from '../../../orders/pages/order-view/constants';
import ToastifyService from '../../../../../services/ToastifyService';

export class UserOrdersWidgetStore {
  areOrdersLoading = false;
  hasError = false;
  page = 0;
  limit = 0;
  total = 0;
  orders: IOrdersListItem[] = [];

  filters: Omit<IRetrieveOrdersRequest, 'userId' | 'page' | 'limit'> = {};

  availableTabs: { name: string; value: string }[] = [];
  isCreatingOrder = false;

  readonly orderStatuses: { key: string; value: string }[] = Object.entries(OrderStatusToTextMapping)
    .filter(
      ([value]) =>
        (Number(value) as unknown as plt.core.base.ORDER_STATUS_TYPE) !==
        plt.core.base.ORDER_STATUS_TYPE.UNKNOWN_ORDER_STATUS_TYPE,
    )
    .map(([value, key]) => ({ key, value }));

  constructor(readonly userId: string, readonly serviceProvider: IUserOrdersServiceProvider) {
    makeAutoObservable(this, {}, { autoBind: true });

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.loadOrders({ page: 1, limit: 5, userId });
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.loadAvailableTabs();
  }

  get getOrdersInformation(): IOrderRow[] {
    return this.orders.map((order) => ({
      componentKey: order.order?.orderId!,
      cart: order.cart,
      address: order.address,
      order: order.order,
      assignments: order.assignments,
      cost: order.cost,
    }));
  }

  public updateLoadParams = (fetchOptions: IHandlerList): void => {
    const fetchState: IRetrieveOrdersRequest = {
      page: fetchOptions.value?.pagination?.page || 1,
      limit: fetchOptions.value?.pagination?.limit || 5,
      userId: this.userId,
      statuses: this.filters.statuses,
    };

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.loadOrders(fetchState);
  };

  private async loadOrders(args: IRetrieveOrdersRequest) {
    this.areOrdersLoading = true;
    this.hasError = false;

    try {
      const { data, page, total, limit } = await this.serviceProvider.retrieveOrders(args);

      this.limit = limit;
      this.total = total;
      this.page = page;
      this.orders = data;
    } catch (error) {
      logger.error(error);
      this.hasError = true;

      return null;
    } finally {
      this.areOrdersLoading = false;
    }
  }

  public async createOrder(elementId: string): Promise<number | undefined> {
    this.isCreatingOrder = true;
    try {
      const { orderSerialNumber } = await this.serviceProvider.createOrder({
        customerId: this.userId,
        elementId,
      });

      this.isCreatingOrder = false;
      return orderSerialNumber!;
    } catch (error) {
      ToastifyService.toast(`Ошибка создания заказа – ${(error as Error).message}`, { type: 'error' });
      this.isCreatingOrder = false;
    }
  }

  private async loadAvailableTabs(): Promise<void> {
    try {
      const tabs = await this.serviceProvider.getAvailableTabs();
      this.availableTabs = (tabs.tabs as { name: string; value: string }[]) ?? [];
    } catch (error) {
      logger.error(error);
    }
  }
}
